import React, { useMemo, useState } from 'react'
import { Button } from '@sureco/design-system'
import TerminationUploadModal from '../components/TerminationsUploadModal'
import PageWrapper from '../../../PageWrapper'
import Page from 'layouts/Page'
import { message } from '@surecompanies/core_components'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import { CENSUS_MANAGEMENT_PATH } from '../constants'
import BulkTerminationsCompletedGrid from '../components/BulkTerminationsCompletedGrid'
import ProcessingTerminationsGrid from '../components/ProcessingTerminationsGrid'
import FailedTerminationsGrid from '../components/FailedTerminationsGrid'
import { SimpleCustomerDto } from 'enrollment-platform/api/persons/dto/simple-customer.dto'
import { useCensusV2Clients } from '../hooks/use-census-v2-clients'
import ClientSelect from 'components/ClientSelect'
import { CensusClientFilter } from '../types/census-client-filter'

const BulkTerminationsPage = () => {
  const [modalVisible, setModalVisible] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [selectedClient, setSelectedClient] = useState<
    SimpleCustomerDto | undefined
  >()
  const [filter, setFilter] = useState<CensusClientFilter>({
    includeTestCompanies: true,
  })
  const { clients, error } = useCensusV2Clients()
  if (error) {
    const msg = `Error loading companies: ${error.message}`
    console.error(msg)
    message.error(msg, 5)
  }

  const TERMINATION_FILE_HEADERS = [
    'CompanyEmployeeId',
    'EmploymentStatus',
    'TerminationDate',
    'FirstName',
    'LastName',
    'SSN',
  ]

  const handleDownloadTemplate = () => {
    const csvContent =
      TERMINATION_FILE_HEADERS.join(',') +
      '\n' +
      '4353265jjjj,TERMINATED,5/14/2005,Phillip,Maytwotest,123456789'
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'termination_file_template.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleCompanyChange = (value: string) => {
    const c = clients.find((c) => c.id === value)
    setSelectedClient(c)
    setFilter({ ...filter, selectedClient: c })
  }

  const clientNameLookup = useMemo(() => {
    return clients.reduce((acc, client) => {
      acc[client.id] = client.name
      return acc
    }, {})
  }, [clients])

  return (
    <PageWrapper>
      <Page
        title="Bulk Terminations"
        action={
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button variant="secondary" onClick={handleDownloadTemplate}>
              Download Template
            </Button>
            <ClientSelect
              allCompaniesPlacholder={{ id: '', companyName: 'All Companies' }}
              companies={clients.map(({ name, id }) => ({
                companyName: name,
                id: id,
              }))}
              defaultCompanyId={''}
              selectedCompanyId={selectedClient?.id}
              onChange={handleCompanyChange}
            />
            <Button onClick={() => setModalVisible(true)}>
              Upload Terminations
            </Button>
          </div>
        }
        breadcrumb={
          <BreadcrumbNav
            items={[
              { title: 'Census Management', path: CENSUS_MANAGEMENT_PATH },
              { title: 'Bulk Terminations' },
            ]}
          />
        }
      >
        <TerminationUploadModal
          open={modalVisible}
          onOk={() => {
            setModalVisible(false)
            setRefreshKey((prev) => prev + 1)
          }}
          onCancel={() => setModalVisible(false)}
        />
        <BulkTerminationsCompletedGrid
          clientNameLookup={clientNameLookup}
          refreshKey={refreshKey}
          filter={filter}
        />
        <ProcessingTerminationsGrid
          clientNameLookup={clientNameLookup}
          refreshKey={refreshKey}
          filter={filter}
        />
        <FailedTerminationsGrid
          clientNameLookup={clientNameLookup}
          refreshKey={refreshKey}
          filter={filter}
        />
      </Page>
    </PageWrapper>
  )
}

export default BulkTerminationsPage
